import { type Camera, type Plane, Vector2, type Vector3, type WebGLRenderer } from 'three'
import { LineMaterial, type LineMaterialParameters } from 'three/examples/jsm/lines/LineMaterial.js'
import type { GraphicsBackendKind } from '@/graphics/GraphicsConfig'
import { LineType } from '@/plugins/cad/module/CADModule/model/common/constant'
import type { IEntity } from '@/plugins/cad/module/CADModule/model/entity'
import {
  applyCadLineDashScale,
  type CadLinePattern,
  createCadLineMaterialSync,
  getCadLineBackend,
} from '@/plugins/cad/module/CADModule/object/material/CadLineFactory'
import { Setting } from '@/plugins/cad/module/common/setting'
import {
  type CadViewEnvironment,
  getPixelSizeInWorldByCamera,
} from '@/plugins/cad/module/common/utils'
import { DashDotLineMaterial } from './DashDotLineMaterial'
import { DoubleDashLineMaterial } from './DoubleDashLineMaterial'

function createInitialResolution() {
  if (typeof window === 'undefined') {
    return new Vector2(1, 1)
  }
  return new Vector2(window.innerWidth, window.innerHeight)
}

/**
 * 线型材质通用工具，负责线宽换算、实体样式解析以及按线型创建材质。
 */
export class MaterialUtils {
  /** 全局屏幕分辨率，供线宽计算使用，初始化为窗口像素尺寸 */
  static globalResolution = createInitialResolution()
  private static lineTypeDashScale = Setting.dashSize
  private static lineTypeMaterials = new Set<LineMaterial>()

  /**
   * 将 CAD 线宽（毫米）换算为屏幕像素宽度。
   *
   * @param lineWidthInMM - CAD 线宽，单位毫米
   * @returns 屏幕像素宽度，最小保底 1 像素
   */
  static getPixelWidth(lineWidthInMM: number): number {
    // 定义一个基准：假设 0.25mm 对应屏幕上的 1px (这是 CAD 常用的视觉比例)
    const scale = 4.0
    // 极细线保底 1 像素
    return Math.max(lineWidthInMM * scale, 1.0)
  }

  /**
   * 从实体样式构造 LineMaterial 基础参数。
   *
   * @param entity - 目标实体
   * @returns 包含颜色、线宽、分辨率等字段的材质参数对象
   */
  static getMaterialOptionByEntity(entity: IEntity) {
    return {
      color: entity.color,
      linewidth: MaterialUtils.getPixelWidth(entity.lineWidth),
      resolution: MaterialUtils.globalResolution,
      depthTest: false,
      depthWrite: false,
    }
  }

  static setLineTypeDashScale(dashScale: number) {
    if (Number.isFinite(dashScale) && dashScale > 0) {
      if (Math.abs(dashScale - MaterialUtils.lineTypeDashScale) / dashScale < 0.001) {
        return
      }
      MaterialUtils.lineTypeDashScale = dashScale
      MaterialUtils.syncLineTypeMaterials()
    }
  }

  static resetLineTypeDashScale() {
    MaterialUtils.lineTypeDashScale = Setting.dashSize
    MaterialUtils.syncLineTypeMaterials()
  }

  static getLineTypeDashScaleByCamera(
    camera: Camera,
    source: CadViewEnvironment | WebGLRenderer,
    dashPixelSize = Setting.dashSize,
    referencePoint?: Vector3,
    plane?: Plane
  ): number | undefined {
    const dashWorldSize = getPixelSizeInWorldByCamera(camera, source, dashPixelSize, {
      plane,
      referencePoint,
    })
    if (!Number.isFinite(dashWorldSize) || dashWorldSize <= 0) {
      return
    }

    return 1 / dashWorldSize
  }

  static updateLineTypeDashScale(
    material: LineMaterial,
    dashScale = MaterialUtils.lineTypeDashScale
  ) {
    if (!material.dashed) {
      return
    }
    const currentScale =
      (material as unknown as { dashScale?: number; scale?: number }).dashScale ??
      (material as unknown as { scale?: number }).scale
    if (currentScale === dashScale) return
    applyCadLineDashScale(material, dashScale)
    // 同材质连续绘制时 Three.js 不会自动重复上传 ShaderMaterial uniforms。
    if ('uniformsNeedUpdate' in material) material.uniformsNeedUpdate = true
  }

  private static registerLineTypeMaterial(material: LineMaterial) {
    MaterialUtils.lineTypeMaterials.add(material)
    const dispose = material.dispose.bind(material)
    material.dispose = () => {
      MaterialUtils.lineTypeMaterials.delete(material)
      dispose()
    }
    return material
  }

  private static syncLineTypeMaterials() {
    MaterialUtils.lineTypeMaterials.forEach((material) => {
      MaterialUtils.updateLineTypeDashScale(material)
    })
  }

  /**
   * 按线型创建对应的 LineMaterial 实例。
   *
   * @param lineType - 目标线型，决定是否虚线及所用子类
   * @param materialOption - 基础材质参数
   * @returns 对应线型的线段材质实例
   */
  static createMaterialByLineType(
    lineType: LineType,
    materialOption: LineMaterialParameters,
    backend: GraphicsBackendKind = getCadLineBackend()
  ) {
    const option = { ...materialOption }
    const dashOption = {
      dashSize: 1,
      gapSize: 0.5,
      dashScale: MaterialUtils.lineTypeDashScale,
      dashed: true,
    }
    const dashMaterialOption = {
      ...dashOption,
      ...option,
      dashed: true,
    }
    if (backend === 'webgpu') {
      const nodeOption =
        lineType === LineType.Solid ? option : { ...dashMaterialOption, dashed: true }
      if (lineType === LineType.DoubleDash) {
        const sizedOption = option as LineMaterialParameters & {
          dashSize?: number
          gapSize?: number
        }
        nodeOption.gapSize = sizedOption.gapSize ?? sizedOption.dashSize ?? dashOption.dashSize
        nodeOption.linewidth = Math.max(sizedOption.linewidth ?? 1, 4)
      }
      const pattern: CadLinePattern =
        lineType === LineType.DashDot
          ? 'dashDot'
          : lineType === LineType.DoubleDash
            ? 'doubleDash'
            : 'regular'
      return MaterialUtils.registerLineTypeMaterial(
        createCadLineMaterialSync({
          backend,
          options: nodeOption as LineMaterialParameters,
          pattern,
          createWebGlMaterial: () => {
            throw new Error('CAD WebGPU 线材质不应回退到 GLSL 子类')
          },
        })
      )
    }

    let material: LineMaterial
    switch (lineType) {
      case LineType.Dash:
        material = new LineMaterial(dashMaterialOption)
        break
      case LineType.DashDot:
        material = new DashDotLineMaterial(dashMaterialOption)
        break
      case LineType.DoubleDash:
        dashMaterialOption.gapSize = option.gapSize ?? option.dashSize ?? dashOption.dashSize
        dashMaterialOption.linewidth = Math.max(option.linewidth ?? 1, 4)
        material = new DoubleDashLineMaterial(dashMaterialOption)
        break
      default:
        material = new LineMaterial(option)
    }
    return MaterialUtils.registerLineTypeMaterial(material)
  }
}
